import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { api, Project, ApprovalStatus } from '../services/api';

function getApprovalBadge(status: ApprovalStatus) {
  const map: Record<string, { label: string; cls: string }> = {
    'Draft':             { label: 'Draft',       cls: 'badge badge-neutral' },
    'Director Review':   { label: 'Dir Review',  cls: 'badge badge-warning' },
    'Director Approved': { label: 'Dir ✓',       cls: 'badge badge-success' },
    'Director Denied':   { label: 'Dir Denied',  cls: 'badge badge-danger'  },
    'IT Review':         { label: 'IT Review',   cls: 'badge badge-warning' },
    'IT Approved':       { label: 'IT ✓',        cls: 'badge badge-success' },
    'IT Denied':         { label: 'IT Denied',   cls: 'badge badge-danger'  },
    'EMT Review':        { label: 'EMT Review',  cls: 'badge badge-warning' },
    'AGM Review':        { label: 'EMT Review',  cls: 'badge badge-warning' }, // legacy alias
    'CEO Review':        { label: 'CEO Review',  cls: 'badge badge-warning' },
    'Revision':          { label: 'Revision',    cls: 'badge badge-warning' },
    'Paused':            { label: 'Paused',      cls: 'badge badge-neutral' },
    'Approved':          { label: 'Approved',    cls: 'badge badge-success' },
    'Denied':            { label: 'Denied',      cls: 'badge badge-danger'  },
  };
  const b = map[status] || { label: status, cls: 'badge badge-neutral' };
  return <span className={b.cls}>{b.label}</span>;
}

function PriorityDot({ priority }: { priority: string }) {
  const cls = priority === 'High' ? 'p-high' : priority === 'Low' ? 'p-low' : 'p-normal';
  return <span className={`priority-dot ${cls}`} />;
}

// Status timeline pipeline — updated for EMT
function Pipeline({ status }: { status: ApprovalStatus }) {
  const dirDone    = ['Director Approved','EMT Review','AGM Review','IT Review','IT Approved','CEO Review','Approved','Denied'].includes(status);
  const dirActive  = status === 'Director Review';
  const dirDenied  = status === 'Director Denied';
  const emtDone    = ['IT Review','IT Approved','CEO Review','Approved'].includes(status);
  const emtActive  = status === 'EMT Review' || status === 'AGM Review';
  const emtDenied  = status === 'EMT Denied' || status === 'AGM Denied';
  const itDone     = ['CEO Review','Approved'].includes(status);
  const itActive   = status === 'IT Review';
  const itDenied   = status === 'IT Denied';

  const nodeStyle = (done: boolean, active: boolean, denied: boolean): React.CSSProperties => ({
    fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 500,
    background: denied ? 'var(--danger-bg)' : done ? 'var(--success-bg)' : active ? 'var(--info-bg)' : 'var(--surface2)',
    color: denied ? 'var(--danger-text)' : done ? 'var(--success-text)' : active ? 'var(--accent)' : 'var(--text3)',
    border: `1px solid ${denied ? 'var(--danger)' : done ? 'var(--success)' : active ? 'var(--accent)' : 'var(--border2)'}`,
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <span style={nodeStyle(dirDone, dirActive, dirDenied)}>{dirDenied ? 'Dir ✗' : dirDone ? 'Dir ✓' : 'Dir'}</span>
      <span style={{ color: 'var(--text3)', fontSize: 10 }}>›</span>
      <span style={nodeStyle(emtDone, emtActive, emtDenied)}>{emtDenied ? 'EMT ✗' : emtDone ? 'EMT ✓' : 'EMT'}</span>
      <span style={{ color: 'var(--text3)', fontSize: 10 }}>›</span>
      <span style={nodeStyle(itDone, itActive, itDenied)}>{itDenied ? 'IT ✗' : itDone ? 'IT ✓' : 'IT'}</span>
    </div>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(n: number | null) {
  if (!n) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0 });
}

// Days since a date string
function daysSince(iso: string | null): number {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

type Filter = 'All' | 'Pending' | 'Approved' | 'Mine';

function getAttentionStatuses(masqueradeRole: string | null): string[] {
  if (masqueradeRole === 'Director')    return ['Director Review'];
  if (masqueradeRole === 'EMT')         return ['EMT Review', 'AGM Review'];
  if (masqueradeRole === 'IT.Approver') return ['IT Review'];
  return ['Director Review', 'IT Review', 'EMT Review', 'AGM Review'];
}

function getRoleVisibleProjects(projects: Project[], masqueradeRole: string | null, userEmail: string): Project[] {
  if (masqueradeRole === 'Staff') return projects.filter(p => p.RequestorEmail === userEmail);
  return projects;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, masqueradeRole, isMasquerading } = useAuth();

  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [draftProjects, setDraftProjects] = useState<Project[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [retryCount, setRetryCount]   = useState(0);
  const [filter, setFilter]           = useState<Filter>('All');

  const firstName = user?.name?.split(' ')[0] || 'there';
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today     = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  useEffect(() => {
    if (masqueradeRole === 'Staff') setFilter('Mine');
    else setFilter('All');
  }, [masqueradeRole]);

  const loadProjects = useCallback(() => {
    setLoading(true);
    setError(null);
    api.projects.list()
      .then(data => { setAllProjects(data); setRetryCount(0); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    // Drafts are excluded from the main list endpoint (IsDraft=0); fetch caller's own
    // drafts separately. Non-fatal: failure just hides the box.
    api.projects.myDrafts()
      .then(setDraftProjects)
      .catch(() => setDraftProjects([]));
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  function deleteDraft(id: number, title: string) {
    if (!window.confirm(`Delete draft "${title || 'Untitled draft'}"? This cannot be undone.`)) return;
    api.projects.deleteDraft(id)
      .then(() => setDraftProjects(prev => prev.filter(p => p.ProjectID !== id)))
      .catch(e => window.alert(e.message || 'Failed to delete draft'));
  }

  const projects          = getRoleVisibleProjects(allProjects, masqueradeRole, user?.email || '');
  const attentionStatuses = getAttentionStatuses(masqueradeRole);

  // Stat counts
  const active        = projects.filter(p => p.ProjectStatus === 'In Progress').length;
  const pending       = projects.filter(p => attentionStatuses.includes(p.ApprovalStatus)).length;
  const completed     = projects.filter(p => p.ProjectStatus === 'Completed').length;
  const needsRevision = projects.filter(p => p.ApprovalStatus === 'Revision' && p.RequestorEmail === user?.email).length;
  const totalBudget   = projects
    .filter(p => p.AmountBudgeted && p.ApprovalStatus === 'Approved')
    .reduce((sum, p) => sum + (p.AmountBudgeted || 0), 0);

  // Stat cards — role-aware
  const stats = masqueradeRole === 'Staff'
    ? [
        { value: projects.length,            label: 'My Submissions',   trend: 'All time',       cls: 'trend-info' },
        { value: needsRevision,              label: 'Need Revision',    trend: 'Action required', cls: needsRevision > 0 ? 'trend-warn' : 'trend-up' },
        { value: completed,                  label: 'Completed',        trend: 'All time',        cls: 'trend-up' },
        { value: formatCurrency(totalBudget), label: 'Approved Budget', trend: 'Approved only',   cls: 'trend-info' },
      ]
    : [
        { value: active,                      label: 'Active Projects',  trend: `${projects.filter(p => p.ProjectStatus === 'Not Started').length} not started`, cls: 'trend-up'   },
        { value: pending,                     label: 'Pending Approval', trend: 'Awaiting review',        cls: pending > 0 ? 'trend-warn' : 'trend-up' },
        { value: completed,                   label: 'Completed',        trend: 'All time',               cls: 'trend-up'   },
        { value: formatCurrency(totalBudget), label: 'Approved Budget',  trend: 'Approved projects only', cls: 'trend-info' },
      ];

  const availableFilters: Filter[] = masqueradeRole === 'Staff'
    ? ['Mine']
    : ['All', 'Pending', 'Approved', 'Mine'];

  const filtered = projects.filter(p => {
    if (p.IsDraft) return false; // drafts shown separately
    if (filter === 'Pending')  return attentionStatuses.includes(p.ApprovalStatus);
    if (filter === 'Approved') return p.ApprovalStatus === 'Approved';
    if (filter === 'Mine')     return p.RequestorEmail === user?.email;
    return true;
  });

  const revisionProjects = allProjects.filter(p => p.ApprovalStatus === 'Revision' && p.RequestorEmail === user?.email);
  const needsAttention   = projects.filter(p => attentionStatuses.includes(p.ApprovalStatus)).length;

  const roleContext = isMasquerading
    ? masqueradeRole === 'Staff'       ? 'Viewing as Staff — only your submitted projects are visible'
    : masqueradeRole === 'Director'    ? 'Viewing as Director — projects pending Director approval highlighted'
    : masqueradeRole === 'EMT'         ? 'Viewing as EMT — projects pending EMT approval highlighted'
    : masqueradeRole === 'IT.Approver' ? 'Viewing as IT Approver — projects pending IT review highlighted'
    : ''
    : '';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{greeting}, {firstName}</h1>
          <p className="page-sub">
            {isMasquerading
              ? roleContext
              : needsAttention > 0
                ? `${needsAttention} project${needsAttention > 1 ? 's' : ''} need${needsAttention === 1 ? 's' : ''} attention · ${today}`
                : `All caught up · ${today}`
            }
          </p>
        </div>
        <button className="btn btn-primary btn-lg" onClick={() => navigate('/new')}>
          + New Project Request
        </button>
      </div>

      {/* ── Stat cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="stat-card" style={{ opacity: 0.5 }}>
                <div className="stat-value">—</div>
                <div className="stat-label">Loading...</div>
              </div>
            ))
          : stats.map((s, i) => (
              <div key={i} className="stat-card">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
                <div className={`stat-trend ${s.cls}`}>{s.trend}</div>
              </div>
            ))
        }
      </div>

      {/* ── Revision requested banner ── */}
      {revisionProjects.length > 0 && (
        <div style={{
          background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning, #f59e0b)',
          borderRadius: 10, padding: '14px 18px', marginBottom: 16,
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--warning-text, #92400e)', marginBottom: 8 }}>
            ✏️ {revisionProjects.length} project{revisionProjects.length > 1 ? 's' : ''} need{revisionProjects.length === 1 ? 's' : ''} revision
          </div>
          {revisionProjects.map(p => (
            <div key={p.ProjectID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.Title}</div>
                {p.RevisionComment && (
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>"{p.RevisionComment}"</div>
                )}
              </div>
              <button className="btn btn-sm" onClick={() => navigate(`/new?revise=${p.ProjectID}`)}>
                Revise & Resubmit →
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Draft projects ── */}
      {draftProjects.length > 0 && (
        <div style={{
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '14px 18px', marginBottom: 16,
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
            📝 {draftProjects.length} saved draft{draftProjects.length > 1 ? 's' : ''}
          </div>
          {draftProjects.map(p => (
            <div key={p.ProjectID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.Title || 'Untitled draft'}</div>
                {p.LastDraftSavedAt && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                    Last saved {formatDate(p.LastDraftSavedAt)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" onClick={() => navigate(`/new?draft=${p.ProjectID}`)}>
                  Resume →
                </button>
                <button
                  className="btn btn-sm"
                  style={{ color: 'var(--danger-text, #991b1b)' }}
                  onClick={() => deleteDraft(p.ProjectID, p.Title)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Projects table ── */}
      <div className="card">
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {filter === 'All' ? 'All Projects' : filter === 'Mine' ? 'My Requests' : filter + ' Projects'}
            <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text2)', marginLeft: 8 }}>
              {loading ? 'Loading...' : `${filtered.length} ${filtered.length === 1 ? 'project' : 'projects'}`}
            </span>
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {availableFilters.map(f => (
              <span key={f} onClick={() => setFilter(f)} style={{
                fontSize: 12, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                border: '1px solid var(--border2)',
                background: filter === f ? 'var(--accent)' : 'var(--surface)',
                color:      filter === f ? '#fff' : 'var(--text2)',
                transition: 'all 0.15s',
              }}>{f}</span>
            ))}
          </div>
        </div>

        {error && !loading && (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ color: 'var(--danger-text)', marginBottom: 12, fontSize: 13 }}>
              Failed to load projects — the database may be waking up.
            </div>
            <button className="btn btn-primary" onClick={() => { setRetryCount(c => c + 1); loadProjects(); }}>
              🔄 Retry {retryCount > 0 ? `(attempt ${retryCount + 1})` : ''}
            </button>
          </div>
        )}

        {loading && (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 8 }}>
              {retryCount > 0 ? `Retrying... (attempt ${retryCount + 1})` : 'Loading projects...'}
            </div>
            <div style={{ color: 'var(--text3)', fontSize: 12 }}>This may take a moment if the database is waking up.</div>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state" style={{ padding: 48 }}>
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No projects found</div>
            <div className="empty-state-sub">
              {filter === 'Mine'
                ? masqueradeRole === 'Staff'
                  ? 'This staff member has no submitted projects.'
                  : "You haven't submitted any projects yet."
                : 'No projects match this filter.'}
            </div>
            {filter === 'Mine' && (
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/new')}>
                Submit Your First Request
              </button>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Department</th>
                <th>Priority</th>
                <th>Approval Pipeline</th>
                <th>Budget</th>
                <th>Target Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const days = daysSince(p.CreatedAt);
                const isPending = attentionStatuses.includes(p.ApprovalStatus);
                const stale = isPending && days >= 7;
                return (
                  <tr key={p.ProjectID} onClick={() => navigate(`/projects/${p.ProjectID}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.Title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{p.RequestorName}</div>
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: 13 }}>{p.DepartmentName}</td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <PriorityDot priority={p.Priority} />{p.Priority}
                      </span>
                    </td>
                    <td><Pipeline status={p.ApprovalStatus} /></td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{formatCurrency(p.AmountBudgeted)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {p.EndDate ? formatDate(p.EndDate) : '—'}
                      {stale && (
                        <span style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 600,
                          color: days >= 14 ? 'var(--danger-text, #991b1b)' : 'var(--warning-text, #92400e)',
                          background: days >= 14 ? 'var(--danger-bg)' : 'var(--warning-bg)',
                          padding: '1px 5px', borderRadius: 4,
                        }}>{days}d</span>
                      )}
                    </td>
                    <td>{getApprovalBadge(p.ApprovalStatus)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
